<template>
    <div>
        <div class="modal" tabindex="-1" role="dialog" id="muven-sales-peak-filter-modal">
            <div class="modal-dialog" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Filtro de pico de vendas</h5>
                    </div>

                    <div class="modal-body">
                        <div>
                            <div class="row">
                                <div class="col-12 col-md-6">
                                    <div class="form-group mb-0">
                                        <label>Data inicial</label>
                                        <v-date-picker 
                                            :input-props="{'class': 'form-control'}" 
                                            popover-visibility="focus"
                                            v-model="salesPeakFilter.date.start" />
                                    </div>
                                </div>

                                <div class="col-12 col-md-6">
                                    <div class="form-group mb-0">
                                        <label>Data final</label>
                                        <v-date-picker 
                                            :input-props="{'class': 'form-control'}" 
                                            popover-visibility="focus"
                                            v-model="salesPeakFilter.date.end" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button type="button" 
                            class="btn btn-primary"
                            :disabled="loadingFilter"
                            @click="getInfoByFilter(salesPeakFilter)">
                            Filtrar

                            <fa icon="sync-alt" spin v-show="loadingFilter"/>
                        </button>

                        <button type="button" 
                            class="btn btn-outline-secondary" 
                            @click="clearFilter(salesPeakFilter)">Restaurar</button>

                        <button type="button" 
                            class="btn btn-outline-secondary" 
                            @click="closeFilterModal(ID_MODAL_FILTER)">Fechar</button>
                    </div>
                </div>
            </div>
        </div>

        <div class="muven-card">
            <div class="muven-card-title">
                <p>
                    Pico de vendas por hora
                    <button type="button" 
                        class="btn btn-outline-secondary btn-sm float-right"
                        @click="showFilterModal(ID_MODAL_FILTER)">
                        <fa icon="calendar-alt" />
                    </button>
                </p>

                <p class="muven-card-subtitle">
                    <span>{{salesPeakFilter.dateSubmitted.start | dateFilter}}</span> até <span>{{salesPeakFilter.dateSubmitted.end | dateFilter}}</span>
                    <span v-show="salesPeakFilter.hasChannelList()">
                        <br>
                        <span>Canais: {{salesPeakFilter.getChannelListDescription()}}</span>
                    </span>
                    <span v-show="salesPeakFilter.hasStoreList()">
                        <br>
                        <span>Lojas: {{salesPeakFilter.getStoreListDescription()}}</span>
                    </span>
                </p>
            </div>

            <div>
                <highcharts :options="salesPeakChartOptions" ref="chart" :class="{'graphic-style-mobile': isMobile}"/>
            </div>
        </div>
    </div>    
</template>

<script>
import Highcharts from 'highcharts'
import modalService from '@/utils/services/ModalService'
import dashboardService from '@/dashboard/services/DashboardService'
import {DashboardSalesPeakFilterModel} from '@/dashboard/models/DashboardSalesPeakFilterModel'
import { dateFilter } from '@/utils/filters/DateTimeFilter'
import dashboardFilterMixin from '@/dashboard/mixins/DashboardFilterMixin'
import { createGraphic } from '@/utils/normalizers/GraphicNormalizer'
import { chartHeatStops } from '@/utils/constants/ChartTheme'
import {times} from 'lodash' 
import { hoursDay } from '@/utils/constants/HoursDay'
import { getShortWeekDaysTransformed } from '@/utils/constants/WeekDays'

const ID_MODAL_FILTER = '#muven-sales-peak-filter-modal'

export default {
    name: 'SalesPeakCmp',
    props: {
        isMobile: {
            type: Boolean,
            default: false,
        },
    },
    mixins: [
        dashboardFilterMixin,
    ],
    data() {
        return {
            ID_MODAL_FILTER,
            loadingFilter: false,
            salesPeakFilter: new DashboardSalesPeakFilterModel(),
            salesPeakList: [],
            salesPeakChartOptions: createGraphic({
                chart: {
                    type: 'heatmap',
                    marginTop: 25,
                    marginBottom: 60,
                    plotBorderWidth: 0,
                },

                xAxis: {
                    categories: [],
                    opposite: true,
                },

                yAxis: {
                    categories: [],
                    title: null,
                    reversed: true,
                },

                colorAxis: {
                    min: 0,
                    stops: chartHeatStops(),
                },

                tooltip: {
                    formatter() {
                        return `Vendas: <b>${this.point.value}</b>
                                <br>
                                Dia: <b>${this.series.yAxis.categories[this.point.y]}</b>
                                <br>
                                Horário: <b>${this.series.xAxis.categories[this.point.x]}</b>`;
                    }
                },

                legend: {
                    enabled: true,
                    align: 'right',
                    layout: 'horizontal',
                    margin: 0,
                    verticalAlign: 'bottom',
                    y: 15,
                    x: 5,
                    symbolWidth: 320,
                    symbolHeight: 8,
                    symbolRadius: 4,
                },

                series: [{
                    name: 'Sales per employee',
                    // Heatmap data:
                    // [xAxisPos, yAxisPos, cellValue]
                    data: [],
                    dataLabels: {
                        enabled: true,
                        style: {
                            fontSize: '12px',
                            fontWeight: '600',
                            textOutline: 'none',
                        },
                    }
                }],
            }),
        }
    },
    filters: {
        dateFilter,
    },
    methods: {
        async getInfoByFilter(filter) {
            this.loadingFilter = true

            filter.fillDateWhenEmpty()
            filter.updatesFilterSubmitted()

            await this.drawGraphic(filter)

            this.loadingFilter = false
            this.closeFilterModal(this.ID_MODAL_FILTER)
        },
        async drawGraphic(filter) {
            this.salesPeakFilter.updatesFilterSubmittedWith(filter)
            this.salesPeakList = await dashboardService.getSalesPeak(filter)
            
            if (this.isMobile) {
                this._drawGraphicMobile(this.salesPeakList)
            } else {
                this._drawGraphicDesktop(this.salesPeakList)
            }
        },
        _drawGraphicMobile(salesPeakList) {
            this.$refs.chart.chart.yAxis[0].setCategories(hoursDay)
            this.$refs.chart.chart.xAxis[0].setCategories(getShortWeekDaysTransformed())

            const dataArr = []

            salesPeakList.forEach((d, indexDays) => {
                d.hours.forEach((h, indexHours) => {
                    dataArr.push([
                        indexDays,  // xAxis
                        indexHours, // yAxis
                        h.quantity, // cellValue
                    ])
                })
            })

            this.$refs.chart.chart.series[0].setData(dataArr)
        },
        _drawGraphicDesktop(salesPeakList) {
            this.$refs.chart.chart.xAxis[0].setCategories(hoursDay)
            this.$refs.chart.chart.yAxis[0].setCategories(getShortWeekDaysTransformed())

            const dataArr = []

            salesPeakList.forEach((d, indexDays) => {
                d.hours.forEach((h, indexHours) => {
                    dataArr.push([
                        indexHours, // xAxis
                        indexDays,  // yAxis
                        h.quantity, // cellValue
                    ])
                })
            })

            this.$refs.chart.chart.series[0].setData(dataArr)
        },
    },
    async mounted() {
        await this.drawGraphic(this.salesPeakFilter)
    },
}
</script>

<style lang="scss" scoped>
.graphic-style-mobile {
    height: 1000px;
}
</style>